<!-- frontend/src/components/Financeiro/Conciliacao/ConciliacaoBancoOperadoraModal.vue -->
<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container modal-lg">
      <div class="modal-header">
        <h6>{{ tituloModal }}</h6>
        <i class="bi bi-x-circle close-icon" @click="$emit('close')"></i>
      </div>

      <div class="spacer"></div>

      <div class="modal-content">
        <div class="info-box mb-3">
          <div><strong>ID OFX:</strong> {{ item?.id }}</div>
          <div><strong>Data:</strong> {{ formatDate(item?.dt_movimento) }}</div>
          <div><strong>Valor bancário:</strong> {{ formatCurrency(item?.valor) }}</div>
          <div><strong>Memo:</strong> {{ item?.memo || '—' }}</div>
        </div>

        <div class="info-box mb-3">
          <div><strong>Soma selecionada:</strong> {{ formatCurrency(somaSelecionada) }}</div>
          <div>
            <strong>Diferença:</strong>
            <span :class="differenceClass">
              {{ formatCurrency(diferenca) }}
            </span>
          </div>
        </div>

        <div v-if="erro" class="alert alert-danger py-2 mb-3">
          {{ erro }}
        </div>

        <div v-if="loading" class="text-muted py-3">
          Carregando candidatos...
        </div>

        <div v-else class="mb-3">
          <div class="table-scroll">
            <div class="table-responsive">
              <table class="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th></th>
                    <th>Evento</th>
                    <th>Venc.</th>
                    <th>Autorização</th>
                    <th>NSU</th>
                    <th>Modalidade</th>
                    <th class="text-end">Líquido</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!candidatos.length">
                    <td colspan="8" class="text-center text-muted py-4">
                      Nenhum candidato encontrado.
                    </td>
                  </tr>

                  <tr v-for="cand in candidatos" :key="cand.id">
                    <td>
                      <input
                        type="checkbox"
                        :checked="isSelecionado(cand)"
                        :disabled="isVisualizacao"
                        @change="toggleSelecionado(cand)"
                      />
                    </td>
                    <td>{{ cand.tipo_evento }}</td>
                    <td>{{ formatDate(cand.data_vencimento || cand.data_venda) }}</td>
                    <td>{{ cand.autorizacao || '—' }}</td>
                    <td>{{ cand.nsu || '—' }}</td>
                    <td>{{ cand.modalidade || '—' }}</td>
                    <td class="text-end">{{ formatCurrency(cand.valor_liquido) }}</td>
                    <td>{{ cand.score ?? '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="form-group mb-3">
          <label>Observação:</label>
          <textarea
            v-model="observacao"
            class="form-control"
            rows="3"
            :disabled="isVisualizacao"
          ></textarea>
        </div>

        <div class="d-flex justify-content-end mt-4 mb-2">
          <button class="btn btn-secondary me-2" @click="$emit('close')" :disabled="salvando">
            {{ isVisualizacao ? 'Fechar' : 'Cancelar' }}
          </button>

          <button
            v-if="!isVisualizacao"
            class="btn btn-primary"
            @click="confirmar"
            :disabled="salvando || !selecionados.length || !podeConfirmar"
          >
            {{ salvando ? 'Salvando...' : 'Confirmar conciliação' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import {
  listarCandidatosBancoOperadora,
  conciliarManualBancoOperadora
} from '@/services/conciliacaoBancaria.api'

const props = defineProps({
  item: { type: Object, required: true },
  modo: { type: String, default: 'edicao' },
  vinculados: { type: Array, default: () => [] }
})

const emit = defineEmits(['close', 'salvo'])

const loading = ref(false)
const salvando = ref(false)
const erro = ref('')
const candidatos = ref([])
const selecionados = ref([])
const observacao = ref('')

const valorItem = computed(() => Number(props.item?.valor || 0))

const somaSelecionada = computed(() =>
  selecionados.value.reduce((acc, item) => acc + Number(item.valor_liquido || 0), 0)
)

const diferenca = computed(() =>
  Number((valorItem.value - somaSelecionada.value).toFixed(2))
)

const podeConfirmar = computed(() => Math.abs(diferenca.value) <= 0.01)

const differenceClass = computed(() => {
  const n = Math.abs(diferenca.value)
  return n <= 0.01 ? 'diff-ok' : 'diff-alert'
})

const isVisualizacao = computed(() => props.modo === 'visualizacao')

const tituloModal = computed(() =>
  isVisualizacao.value ? 'Detalhar conciliação Banco x Operadora' : 'Conciliação Banco x Operadora'
)

onMounted(() => {
  if (isVisualizacao.value) {
    candidatos.value = Array.isArray(props.vinculados) ? props.vinculados : []
    selecionados.value = Array.isArray(props.vinculados) ? [...props.vinculados] : []
    observacao.value = props.vinculados?.[0]?.observacao || ''
    return
  }

  load()
})

async function load() {
  loading.value = true
  erro.value = ''
  try {
    const { data } = await listarCandidatosBancoOperadora({
      ofxItemId: props.item.id
    })
    candidatos.value = Array.isArray(data?.candidatos) ? data.candidatos : []
  } catch (e) {
    erro.value = e?.response?.data?.error || 'Erro ao carregar candidatos Banco x Operadora.'
  } finally {
    loading.value = false
  }
}

function isSelecionado(item) {
  return selecionados.value.some((s) => s.id === item.id)
}

function toggleSelecionado(item) {
  if (isSelecionado(item)) {
    selecionados.value = selecionados.value.filter((s) => s.id !== item.id)
  } else {
    selecionados.value = [...selecionados.value, item]
  }
}

async function confirmar() {
  if (!selecionados.value.length || !podeConfirmar.value) return

  salvando.value = true
  erro.value = ''

  try {
    await conciliarManualBancoOperadora({
      ofx_item_id: props.item.id,
      operadora_itens: selecionados.value.map((item) => item.id),
      observacao: observacao.value || null
    })

    emit('salvo')
    emit('close')
  } catch (e) {
    erro.value = e?.response?.data?.error || 'Erro ao salvar conciliação Banco x Operadora.'
  } finally {
    salvando.value = false
  }
}

function formatDate(v) {
  if (!v) return '—'
  const [y, m, d] = String(v).slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(v || 0))
}
</script>

<style scoped>
.modal-header {
  background: #ccc;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  margin: 0 10px;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-content {
  padding: 15px;
  overflow: hidden;
}

.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}

.spacer {
  margin-bottom: 20px;
}

.form-group label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.form-control {
  width: 100%;
  padding: 5px;
  border: 1px solid #ced4da;
  border-radius: 0;
}

.info-box {
  border: 1px solid #ced4da;
  padding: 10px 12px;
  background: #f8f9fa;
}

.table-scroll {
  max-height: 320px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ced4da;
}

.table {
  margin-bottom: 0;
}

.table th,
.table td {
  vertical-align: middle;
}

.diff-ok {
  color: #166534;
  font-weight: 700;
}

.diff-alert {
  color: #b91c1c;
  font-weight: 700;
}

input[type="checkbox"] {
  transform: scale(0.8);
  transform-origin: center;
  cursor: pointer;
  margin: 0;
}

@media (max-width: 768px) {
  .modal-container {
    max-width: 100%;
    max-height: 95vh;
  }
}
</style>